<script>
import ContentBlockMixin from '@shared/mixins/ContentBlock';
import TalkizyAPI from '@app/services/API/Talkizy';
import ContentBlockTalkizyMeetingView from '@shared/components/ContentBlocks/Views/TalkizyMeeting.vue';

export default {
  mixins: [ContentBlockMixin],
  components: { ContentBlockTalkizyMeetingView },
  static: {
    data: {
      meetingId: '',
      title: '',
      startsAt: '',
      endsAt: '',
      duration: null,
      timezone: '',
      animatorFirstname: '',
      // Invite automatiquement les nouveaux apprenants STUDENT de la formation à
      // la classe (tant qu'elle n'est ni commencée ni annulée). Voir back :
      // AutoInviteToTalkizyListener.
      autoInvite: false,
    },
  },
  data: () => ({
    isForm: true,
    meetings: [],
    isFetching: false,
    // Statut « annulé » de la classe sélectionnée, détecté à l'ouverture :
    // l'aperçu ne fait pas d'appel live, on le lui impose donc ici.
    isCancelled: false,
  }),
  computed: {
    isConnected() {
      return this.$store.getters['auth/isTalkizyConnected'];
    },
  },
  created() {
    // Le bloc n'est ajouté que si Talkizy est connecté (garde dans addBlock) ;
    // ici on charge simplement les classes disponibles.
    if (this.isConnected) {
      this.fetchMeetings();
    }
    // Bloc déjà rempli (édition) : vérifier si la classe a été annulée depuis.
    this.checkCancelled(this.newData.meetingId);
  },
  watch: {
    'newData.meetingId': function(id) {
      // Nouvelle sélection dans une classe à venir : jamais annulée.
      this.isCancelled = false;

      const meeting = this.meetings.find((m) => String(m.meeting_id) === String(id));
      if (!meeting) return;

      // Snapshot pour affichage instantané (éditeur + apprenant), rafraîchi
      // en live côté apprenant.
      this.newData.title = meeting.title;
      this.newData.startsAt = meeting.starts_at;
      this.newData.endsAt = meeting.ends_at;
      this.newData.duration = meeting.duration || null;
      this.newData.timezone = meeting.timezone || '';
      this.newData.animatorFirstname = (this.$store.getters['auth/user'] || {}).firstname || '';
    },
  },
  methods: {
    async checkCancelled(meetingId) {
      this.isCancelled = false;
      if (!this.isConnected || !meetingId) return;

      try {
        const meeting = await TalkizyAPI.getMeeting(meetingId);
        this.isCancelled = !!meeting && meeting.status === 'cancelled';
      } catch (e) {
        // Fail-closed : en cas d'erreur on garde l'aperçu snapshot habituel.
        this.isCancelled = false;
      }
    },
    async fetchMeetings() {
      this.isFetching = true;
      try {
        const res = await TalkizyAPI.listMeetings({
          time: 'upcoming', per_page: 100, sort_by: 'starts_at', order: 'asc',
        });
        this.meetings = res.data || [];
      } catch (e) {
        this.meetings = [];
      } finally {
        this.isFetching = false;
      }
    },
    meetingLabel(meeting) {
      const date = this.$moment.utc(meeting.starts_at).local().format('DD/MM/YY [à] HH:mm');
      return `${meeting.title} — ${date}`;
    },
  },
  i18n: {
    messages: {
      fr: {
        talkizy_block: {
          heading: 'Classe virtuelle Talkizy',
          select_label: 'Sélectionnez une classe virtuelle à venir. Tous les apprenants de cette formation verront ce contenu, mais seuls ceux invités à la classe virtuelle pourront la rejoindre.',
          select_placeholder: 'Choisir une classe virtuelle',
          no_meetings: 'Vous n\'avez aucune classe virtuelle à venir. Créez-en une depuis « Classes virtuelles ».',
          go_to_meetings: 'Aller aux classes virtuelles',
          connect_message: 'Pour insérer une classe virtuelle, connectez d\'abord votre espace Teachizy à Talkizy depuis le menu « Classes virtuelles ».',
          auto_invite_label: 'Inviter automatiquement à la classe virtuelle les apprenants qui s\'inscrivent à la formation ou qui y sont ajoutés (tant qu\'elle n\'a pas commencé).',
        },
      },
      en: {
        talkizy_block: {
          heading: 'Talkizy virtual class',
          select_label: 'Select an upcoming virtual class',
          select_placeholder: 'Choose a virtual class',
          no_meetings: 'You have no upcoming virtual class. Create one from "Virtual classes".',
          go_to_meetings: 'Go to virtual classes',
          connect_message: 'To insert a virtual class, first connect your workspace to Talkizy from the "Virtual classes" page.',
          auto_invite_label: 'Automatically invite learners who enroll in (or are added to) the training to the virtual class (while it has not started).',
        },
      },
    },
  },
};
</script>

<template>
  <ContentBlockBase>
    <template #form>
      <div class="field">
        <h3 class="title is-5">
          {{ $t('talkizy_block.heading') }}
        </h3>
      </div>

      <!-- Non connecté à Talkizy -->
      <div v-if="!isConnected" class="box has-text-centered">
        <p class="mb-4">
          {{ $t('talkizy_block.connect_message') }}
        </p>
        <b-button
          type="is-primary is-custom"
          tag="router-link"
          :to="{ name: 'classe_virtuelle' }"
          icon-left="video"
        >
          {{ $t('talkizy_block.go_to_meetings') }}
        </b-button>
      </div>

      <!-- Connecté : sélecteur des classes à venir -->
      <template v-else>
        <b-field :label="$t('talkizy_block.select_label')">
          <b-select
            v-model="newData.meetingId"
            :placeholder="$t('talkizy_block.select_placeholder')"
            :loading="isFetching"
            expanded
            required
          >
            <option
              v-for="meeting in meetings"
              :key="meeting.meeting_id"
              :value="meeting.meeting_id"
            >
              {{ meetingLabel(meeting) }}
            </option>
          </b-select>
        </b-field>

        <p v-if="!isFetching && !meetings.length" class="help">
          {{ $t('talkizy_block.no_meetings') }}
          <router-link :to="{ name: 'classe_virtuelle' }">
            {{ $t('talkizy_block.go_to_meetings') }}
          </router-link>
        </p>

        <!-- MASQUE TEMPORAIREMENT : toggle d'invitation automatique des nouveaux
             inscrits STUDENT a la classe virtuelle. Pour REACTIVER, il suffit de
             decommenter le <b-field> ci-dessous. Le back reste en place
             (AutoInviteToTalkizyListener + index talkizy_auto_invite_meeting_ids
             + guard talkizy-saas) : seule l'option d'activation cote formateur est
             masquee, donc les nouveaux blocs ne pourront plus l'activer.
        <b-field v-if="newData.meetingId" class="mt-4">
          <b-switch v-model="newData.autoInvite">
            {{ $t('talkizy_block.auto_invite_label') }}
          </b-switch>
        </b-field>
        -->
        <!-- FIN MASQUE INVITATION AUTOMATIQUE -->
      </template>
    </template>

    <template #view>
      <ContentBlockTalkizyMeetingView
        class="pevents-none"
        v-bind="viewProps"
        :data="newData"
        :forced-status="isCancelled ? 'cancelled' : null"
      />
    </template>
  </ContentBlockBase>
</template>
